<template>
  <div> <!-- Single root element wrapper -->
    <el-tooltip 
      v-if="symbol" 
      :content="symbol" 
      placement="top"
      ref="tooltipRef"
      :disabled="isScrolling"
      :hide-after="100"
      :show-after="200"
    >
      <div> <!-- Wrapper for tooltip content -->
        <figure @click="$emit('stock-click')" v-if="isPlaceholder" class="stock_logo placeholder"
          @click.stop="goToStockDetail" @touchstart="goToStockDetail" :class="[
            MarketClass,
            {
              placeholderWithContent: placeholderContent,
              postTicker: isPostTicker,
            },
          ]" :style="{
            ...style,
            width: size?.width,
            height: size?.height,
            flex: `0 0 ${size?.width}`,
          }">
          <figcaption v-if="placeholderContent">
            {{ `${placeholderContent}+` }}
          </figcaption>
        </figure>
        <figure @click="$emit('stock-click')" v-else class="stock_logo"
          :class="[MarketClass, { postTicker: isPostTicker }]" @touchstart="goToStockDetail"
          @click.stop="goToStockDetail" :style="{
            ...style,
            width: size?.width,
            height: size?.height,
            flex: `0 0 ${size?.width}`,
          }">
          <img v-if="currentImageUrl" :src="currentImageUrl" @error="handleImageError" :style="{
            width: size?.width,
            height: size?.height,
            flex: `0 0 ${size?.width}`,
          }" />
        </figure>
      </div>
    </el-tooltip>

    <template v-if="!symbol"> <!-- Using template for conditional rendering -->
      <figure v-if="isPlaceholder" class="stock_logo placeholder" @click.stop="goToStockDetail"
        @touchstart="goToStockDetail" :class="{ placeholderWithContent: placeholderContent }" :style="{
          ...style,
          width: size?.width,
          height: size?.height,
          flex: `0 0 ${size?.width}`,
        }">
        <figcaption v-if="placeholderContent">
          {{ `${placeholderContent}+` }}
        </figcaption>
      </figure>
      <figure v-else class="stock_logo" :class="market" @touchstart="goToStockDetail" @click.stop="goToStockDetail"
        :style="{
          ...style,
          width: size?.width,
          height: size?.height,
          flex: `0 0 ${size?.width}`,
        }">
        <img v-if="currentImageUrl" :src="currentImageUrl" @error="handleImageError" :style="{
          width: size?.width,
          height: size?.height,
          flex: `0 0 ${size?.width}`,
        }" />
      </figure>
    </template>
  </div>
</template>

<script lang="ts">
import { useStockSocket } from "@/composables/stock_socket.composable";
import { StockMarkets } from "@/helpers/models/stock/stocks.model";
import { RouteEnum } from "@/router/routeNames";
import utilService from "@/services/util.service";
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  ref,
  watch,
  onUnmounted,
} from "vue";
import { useRouter } from "vue-router";
import { popModal } from "jenesius-vue-modal";
import { useSocketStockData } from "@/composables/stock_data.composable";
import storageService from "@/services/storage.service";
import { useAuth } from "@/composables/public-auth-signin.composable";
import { 
  loadImageWithTracking,
  generateStockImageUrl,
  generateFallbackImageUrl,
  log
} from "@/helpers/image.helpers";
import { trackEvent, trackError } from "@/helpers/tracking.helpers";
import { TrackingEvents } from "@/helpers/enum";

export default defineComponent({
  props: {
    stockId: String,
    imgUrl: String,
    symbol: String,
    market: String,
    isPlaceholder: Boolean,
    size: Object,
    placeholderContent: Number,
    disableRouting: Boolean,
    style: Object,
    hideHover: Boolean,
    sparklineData: Object,
    isPostTicker: Boolean,
    primaryLogoUrl: String,
    secondaryLogoUrl: String,
    tertiaryLogoUrl: String,
  },
  setup(props) {
    const router = useRouter();
    const currentImageUrl = ref<string>('');
    const imageLoadAttempts = ref(0);
    const maxImageLoadAttempts = 3;
    const tooltipRef = ref();
    const isScrolling = ref(false);
    let scrollTimeout: NodeJS.Timeout | null = null;

    // Safe split helper
    function safeSplit(val: any, delimiter: string) {
      return typeof val === 'string' ? val.split(delimiter) : [];
    }

    const getMarketType = () => {
      return props.market || safeSplit(props.stockId || '', "_")[1] || '';
    };

    const generateImageUrls = () => {
      const marketType = getMarketType();
      const mediaUrl = generateStockImageUrl(
        props.symbol || '',
        marketType?.toLowerCase(),
        props.stockId
      );
      const fallbackUrl = generateFallbackImageUrl(marketType);
      // Prefer media.traderverse.io; only keep API logo if it already points there
      const apiPrimary =
        props.primaryLogoUrl?.includes('media.traderverse.io')
          ? props.primaryLogoUrl
          : undefined;
      const apiSecondary =
        props.secondaryLogoUrl?.includes('media.traderverse.io')
          ? props.secondaryLogoUrl
          : undefined;
      const apiTertiary =
        props.tertiaryLogoUrl?.includes('media.traderverse.io')
          ? props.tertiaryLogoUrl
          : undefined;
      return {
        primary: apiPrimary || mediaUrl,
        secondary: apiSecondary || fallbackUrl,
        fallback: apiTertiary || fallbackUrl,
      };
    };

    const loadStockImage = async () => {
      if (!props.symbol || !props.stockId) {
        log('Skipping load - missing symbol or stockId');
        return;
      }
  
      const urls = generateImageUrls();
      const marketType = getMarketType();
      log('Starting stock image load', { symbol: props.symbol, marketType, urls });

      try {
        const result = await loadImageWithTracking({
          src: urls.primary,
          altSrc: urls.secondary,
          userId: props.stockId,
          context: 'stock-logo',
          maxRetries: 2,
          retryDelay: 300,
          persistOnError: true
        });

        if (result.success) {
          log('Primary image loaded successfully', { url: urls.primary });
          currentImageUrl.value = result.loadedSrc;
          trackEvent(TrackingEvents.stockLogoLoadSuccess, {
            stockId: props.stockId,
            loadedUrl: result.loadedSrc,
          });
        } else if (urls.fallback) {
          log('Attempting fallback image load', { fallbackUrl: urls.fallback });
          const fallbackResult = await loadImageWithTracking({
            src: urls.fallback,
            altSrc: '',
            userId: props.stockId,
            context: 'stock-logo-fallback',
            maxRetries: 1,
            retryDelay: 200,
            persistOnError: true
          });
          
          if (fallbackResult.success) {
            log('Fallback image loaded successfully', { url: urls.fallback });
            currentImageUrl.value = fallbackResult.loadedSrc;
          } else {
            log('Fallback image failed to load', { url: urls.fallback });
          }
        }
      } catch (error) {
        log('Error loading stock image', { error });
        trackError(error as Error, {
          context: TrackingEvents.stockLogoLoadError,
          stockId: props.stockId,
          symbol: props.symbol
        });
      }
    };

    const handleImageError = async (event: Event) => {
      log('Image error event triggered', { attempts: imageLoadAttempts.value });
      imageLoadAttempts.value++;
      if (imageLoadAttempts.value >= maxImageLoadAttempts) {
        log('Max attempts reached, hiding image');
        (event.target as HTMLImageElement).style.display = 'none';
        return;
      }
      await loadStockImage();
    };

    // Scroll event handler
    const handleScroll = () => {
      isScrolling.value = true;
      
      // Hide tooltip immediately when scrolling starts
      if (tooltipRef.value) {
        tooltipRef.value.hide?.();
      }
      
      // Clear existing timeout
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      // Set scrolling to false after scroll stops
      scrollTimeout = setTimeout(() => {
        isScrolling.value = false;
      }, 150);
    };

    // Find and attach scroll listeners to parent scroll containers
    const attachScrollListeners = () => {
      const element = tooltipRef.value?.$el;
      if (!element) return;

      let parent = element.parentElement;
      while (parent && parent !== document.body) {
        const computedStyle = window.getComputedStyle(parent);
        const hasScroll = computedStyle.overflowX === 'auto' || 
                         computedStyle.overflowX === 'scroll' ||
                         computedStyle.overflowY === 'auto' || 
                         computedStyle.overflowY === 'scroll' ||
                         parent.classList.contains('leftinfoDvscrollAboutinfodvsss') ||
                         parent.classList.contains('tickersDv');
        
        if (hasScroll) {
          parent.addEventListener('scroll', handleScroll, { passive: true });
        }
        parent = parent.parentElement;
      }
      
      // Also listen to window scroll
      window.addEventListener('scroll', handleScroll, { passive: true });
    };

    const removeScrollListeners = () => {
      const element = tooltipRef.value?.$el;
      if (!element) return;

      let parent = element.parentElement;
      while (parent && parent !== document.body) {
        parent.removeEventListener('scroll', handleScroll);
        parent = parent.parentElement;
      }
      
      window.removeEventListener('scroll', handleScroll);
    };

    const initialObject = () => {
      let stockId = props.stockId;
      const marketType = getMarketType();
      if (marketType === "CRYPTO") {
        stockId = `${props.symbol}_CRYPTO`;
      }
      return {
        _id: props.stockId!,
        type: props?.market,
        symbol: props?.symbol,
        stockId: stockId!,
        logoUrl: props?.imgUrl,
        primaryLogoUrl: props?.primaryLogoUrl,
        secondaryLogoUrl: props?.secondaryLogoUrl,
        tertiaryLogoUrl: props?.tertiaryLogoUrl,
      };
    };

    const { addStockId, checkStockExistence } = useStockSocket();

    onMounted(() => {
      updateStockInfoObject(initialObject());
      loadStockImage();
      
      // Attach scroll listeners after next tick to ensure DOM is ready
      nextTick(() => {
        attachScrollListeners();
      });
    });

    onUnmounted(() => {
      log("Media Unmounted");
      removeScrollListeners();
      
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    });

    const { stockData: stock, updateStockInfoObject } = useSocketStockData(
      initialObject()
    );

    const { requireAuth } = useAuth();

    const goToStockDetail = async (ev: Event) => {
      await requireAuth(
        async () => {
          // User is logged in, proceed to stock detail page
          storageService.RemoveProperty("stockDetail");
          storageService.setStockDetail(stock.value);
          if (!props.disableRouting) {
            popModal();
            setTimeout(() => {
              router.push({
                name: RouteEnum.stockDetail,
                params: { stockId: props.stockId },
              });
            });
          }
        },
        ev
      );
    };

    watch(
      () => [props.stockId, props.symbol, props.market, 
             props.primaryLogoUrl, props.secondaryLogoUrl, props.tertiaryLogoUrl],
      async () => {
        updateStockInfoObject(initialObject());
        currentImageUrl.value = '';
        imageLoadAttempts.value = 0;
        await loadStockImage();
        await nextTick();
      },
      { deep: true }
    );

    const MarketClass = computed(() => {
      return props.market || safeSplit(props.stockId || '', "_")[1] || "market";
    });

    return {
      MarketClass,
      handleImageError,
      stock,
      goToStockDetail,
      currentImageUrl,
      tooltipRef,
      isScrolling
    };
  },
});
</script>

<style lang="scss">
/* Existing styles remain unchanged */
.hoverEffectDv {
  display: none;
  z-index: 99999;

  &:hover {
    z-index: 999;
    display: block;
  }
}

.stock_logo {
  overflow: visible !important;
  padding: 0px;
  border-radius: 10px;
  border: 3px solid transparent;
  cursor: pointer;
  background: var(--color-icon-check);
  border-color: var(--color-icon-check);
  width: 40px;
  flex: 0 0 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &.large {
    width: 40px;
    flex: 0 0 40px;
    height: 40px;
  }
  &.crypto.postTicker,
  &.postTicker {
    width: 28px;
    flex: 0 0 28px;
    height: 28px;
    border-radius: 9px;
    padding: 0px;
      @media screen and (min-width: 1280px) and (max-width: 1891px) {
        width: 22px !important;
        flex: 0 0 22px !important;
        height: 22px !important;
        border-radius: 5px !important;
        padding: 2px !important;
        border-width: 1px !important;
          img{
              border-radius: 4px !important;
          }
      }
  }

  img {
    border-radius: 8px;
    object-fit: contain;
    display: block;
  }

  &.placeholder {
    background: var(--base-dark);
    border-color: var(--card-background-color);
  }

  &:hover {
    .hoverEffectDv {
      display: block;
      z-index: 99999;
    }
  }

  &.ETFs {
    border-color: var(--color-light-purple11) !important;
    background: var(--color-light-purple11) !important;
  }

  &.futures {
    border-color: var(--cyan) !important;
    background: var(--cyan) !important;
  }

  &.crypto {
    border-color: var(--highlight-color) !important;
    background: var(--base-dark) !important;
    padding: 4px !important;
  }

  &.nft {
    border-color: var(--red) !important;
    background: var(--red) !important;
  }

  &.indices {
    border-color: var(--color-new-light-yollow2) !important;
    background: var(--color-new-light-yollow2) !important;
  }

  &.options {
    border-color: var(--pink1) !important;
    background: var(--pink1) !important;
  }
}

.placeholderWithContent {
  text-align: center;
  background-color: var(--modal-background-color) !important;
  border: 3px solid var(--color-grey4) !important;
  padding: 5.6px;
  color: var(--color-new-light-grey2);
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

// @media only screen and (min-width: 1280px){ 
//   .stock_logo {
//     width: 32px !important;
//     flex: 0 0 32px !important;
//     height: 32px !important;
//     border-radius: 8px !important;
//     border-width: 2px !important;
//     padding: 0 !important;

//     img {
//       border-radius: 6px !important;
//     }
//   }
// }

@media only screen and (min-width: 1280px) and (max-width: 1700px) {
  .stock_logo {
    width: 32px !important;
    flex: 0 0 32px !important;
    height: 32px !important;
    border-radius: 8px !important;
    border-width: 2px !important;
    padding: 0 !important;

    img {
      border-radius: 6px !important;
    }
  }
}
</style>